import { c as _c } from "react/compiler-runtime";
import { revalidateLogic } from "@tanstack/react-form";
import { useParams, useRouter } from "next/navigation";
import { startTransition, useRef, useState, useTransition } from "react";
import type z from "zod";
import { SAVE_UTKAST_DEBOUNCE_DELAY } from "@/common/app-config";
import { getAGOversiktHref } from "@/common/route-hrefs";
import { VeiviserSteg } from "@/components/NyPlanSide/LagPlanVeiviser";
import { type OppfolgingsplanFormUnderArbeid, oppfolgingsplanFormUtfylltSchema } from "@/schema/oppfolgingsplanForm/formValidationSchemas";
import { scrollToAppTopForAG } from "@/utils/scrollToAppTop";
import { oppfolgingsplanFormDefaultValues } from "../form-options";
import { useAppForm } from "./form";
import useFerdigstillOppfolgingsplanAction from "./useFerdigstillOppfolgingsplanAction";
import useOppfolgingsplanUtkastLagring from "./useOppfolgingsplanUtkastLagring";
type FormMeta = {
  submitAction: "fortsettTilOppsummering" | "ferdigstill";
};
const defaultMeta: FormMeta = {
  submitAction: "fortsettTilOppsummering"
};
export default function useOppfolgingsplanForm(t0) {
  const $ = _c(20);
  const {
    initialLagretUtkast,
    initialSistLagretTidspunkt
  } = t0;
  const {
    narmesteLederId
  } = useParams();
  const [veiviserSteg, setVeiviserSteg] = useState(VeiviserSteg.FYLL_UT_PLAN);
  const [isPendingProceedToOppsummering, startProceedToOppsummeringTransition] = useTransition();
  const [isPendingExitAndContinueLater, startExitAndContinueLaterTransition] = useTransition();
  const focusThisOnValidationErrorsRef = useRef(null);
  const {
    push
  } = useRouter();
  let t1;
  if ($[0] !== initialLagretUtkast) {
    t1 = {
      ...oppfolgingsplanFormDefaultValues,
      ...initialLagretUtkast
    };
    $[0] = initialLagretUtkast;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialFormValues = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = revalidateLogic();
    t3 = {
      onDynamic: oppfolgingsplanFormUtfylltSchema
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      focusThisOnValidationErrorsRef.current?.focus();
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const form = useAppForm({
    defaultValues: initialFormValues,
    validationLogic: t2,
    validators: t3,
    listeners: {
      onChange: t4 => {
        const {
          formApi
        } = t4;
        return startLagreUtkastIfChanges({
          values: formApi.state.values
        });
      },
      onChangeDebounceMs: SAVE_UTKAST_DEBOUNCE_DELAY
    },
    onSubmitInvalid: t5,
    onSubmitMeta: defaultMeta,
    onSubmit: t6 => {
      const {
        value,
        meta
      } = t6;
      if (meta.submitAction === "fortsettTilOppsummering") {
        saveIfChangesAndProceedToOppsummering(value);
      } else {
        if (meta.submitAction === "ferdigstill") {
          startFerdigstillPlanAction({
            formValues: value as z.infer<typeof oppfolgingsplanFormUtfylltSchema>,
            evalueringsDatoIsoString: value.evalueringsDato,
            includeIkkeMedvirketBegrunnelseFieldInFormSnapshot: value.harDenAnsatteMedvirket === "nei"
          });
        }
      }
    }
  });
  let t7;
  if ($[5] !== initialFormValues || $[6] !== initialSistLagretTidspunkt) {
    t7 = {
      initialFormValues,
      initialSistLagretTidspunkt
    };
    $[5] = initialFormValues;
    $[6] = initialSistLagretTidspunkt;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const {
    isSavingUtkast,
    lagreUtkastError,
    sistLagretTidspunkt,
    startLagreUtkastIfChanges: t8
  } = useOppfolgingsplanUtkastLagring(t7);
  const startLagreUtkastIfChanges = t8;
  const {
    startFerdigstillPlanAction: t9,
    isPendingFerdigstillPlan,
    error: ferdigstillPlanError
  } = useFerdigstillOppfolgingsplanAction();
  const startFerdigstillPlanAction = t9;
  function saveIfChangesAndProceedToOppsummering(values) {
    startProceedToOppsummeringTransition(() => {
      startLagreUtkastIfChanges({
        values,
        onSuccess: () => {
          proceedToOppsummeringSteg();
        }
      });
    });
  }
  const saveIfChangesAndExit = function saveIfChangesAndExit() {
    startExitAndContinueLaterTransition(() => {
      startLagreUtkastIfChanges({
        values: form.state.values,
        onSuccess: () => {
          push(getAGOversiktHref(narmesteLederId));
        }
      });
    });
  };
  function proceedToOppsummeringSteg() {
    startTransition(() => {
      setVeiviserSteg(VeiviserSteg.OPPSUMMERING);
    });
    scrollToAppTopForAG();
  }
  let t10;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function goBackToFyllUtPlanSteg() {
      startTransition(() => {
        setVeiviserSteg(VeiviserSteg.FYLL_UT_PLAN);
      });
      scrollToAppTopForAG();
    };
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const goBackToFyllUtPlanSteg = t10;
  let t11;
  if ($[9] !== ferdigstillPlanError || $[10] !== form || $[11] !== isPendingExitAndContinueLater || $[12] !== isPendingFerdigstillPlan || $[13] !== isPendingProceedToOppsummering || $[14] !== isSavingUtkast || $[15] !== lagreUtkastError || $[16] !== saveIfChangesAndExit || $[17] !== sistLagretTidspunkt || $[18] !== veiviserSteg) {
    t11 = {
      form,
      veiviserSteg,
      focusThisOnValidationErrorsRef,
      isSavingUtkast,
      utkastSistLagretTidspunkt: sistLagretTidspunkt,
      isPendingProceedToOppsummering,
      isPendingExitAndContinueLater,
      isPendingFerdigstillPlan,
      saveIfChangesAndExit,
      goBackToFyllUtPlanSteg,
      lagreUtkastError,
      ferdigstillPlanError
    };
    $[9] = ferdigstillPlanError;
    $[10] = form;
    $[11] = isPendingExitAndContinueLater;
    $[12] = isPendingFerdigstillPlan;
    $[13] = isPendingProceedToOppsummering;
    $[14] = isSavingUtkast;
    $[15] = lagreUtkastError;
    $[16] = saveIfChangesAndExit;
    $[17] = sistLagretTidspunkt;
    $[18] = veiviserSteg;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
}